import React, { useEffect, useState } from 'react';
import {
  formatTimedRunElapsed,
  timedRunElapsedMs,
  useTimedRunClientState,
} from '../../services/TimedRunClientService';
import { windowBridge } from '../framework/windowBridge';
import { ACCENT, COLORS, FONTS, INK } from '../theme';

const TIMER_TICK_MS = 50;

function statusLabel(status: string): string {
  switch (status) {
  case 'finish_pending': return 'SAVING FINISH';
  case 'finished_ranked': return 'OFFICIAL FINISH';
  case 'finished_practice': return 'PRACTICE FINISH';
  case 'invalidated': return 'RUN INVALIDATED';
  case 'abandoned': return 'RUN ENDED';
  default: return 'RELIGHT THE COIL';
  }
}

export const SpeedrunTimerHud: React.FC = () => {
  const state = useTimedRunClientState();
  const [, renderTick] = useState(0);
  const [dismissedRunId, setDismissedRunId] = useState<string | null>(null);
  const run = state.message?.run;

  useEffect(() => {
    if (run?.status !== 'running') return;
    const timer = setInterval(() => renderTick(value => value + 1), TIMER_TICK_MS);
    return () => clearInterval(timer);
  }, [run?.runId, run?.status]);

  if (!run || run.runId === dismissedRunId) return null;

  const terminalFailure = run.status === 'invalidated' || run.status === 'abandoned';
  const isFinished = run.status === 'finished_ranked' || run.status === 'finished_practice' || terminalFailure;
  const accent = terminalFailure
    ? COLORS.danger
    : run.status === 'finished_ranked' || run.status === 'finished_practice'
      ? COLORS.success
      : ACCENT.brassBright;
  const mode = run.rank != null
    ? `#${run.rank}${run.boardTotal != null ? ` / ${run.boardTotal}` : ''}`
    : run.flaskVariant === 'slow'
      ? 'TUNING RUN'
      : run.mode === 'ranked' ? 'RANKED' : 'PRACTICE';

  return (
    <button
      type="button"
      data-testid="speedrun-timer-hud"
      onClick={() => windowBridge.toggle('speedrunRecords')}
      title="Open Coil Records"
      style={{
        position: 'absolute',
        top: 104,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 140,
        pointerEvents: 'auto',
        minWidth: 190,
        padding: '7px 22px 6px 14px',
        borderRadius: 8,
        border: `1px solid ${accent}`,
        background: `linear-gradient(180deg, ${INK.hi}, ${INK.lo})`,
        boxShadow: '0 5px 18px rgba(0, 0, 0, 0.42)',
        color: COLORS.textPrimary,
        fontFamily: FONTS.ui,
        cursor: 'pointer',
      }}
    >
      {isFinished && (
        <span
          role="button"
          tabIndex={0}
          aria-label="Dismiss speedrun banner"
          data-testid="speedrun-timer-dismiss"
          onClick={(e) => {
            e.stopPropagation();
            setDismissedRunId(run.runId);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              setDismissedRunId(run.runId);
            }
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: 6,
            color: COLORS.textMuted,
            fontSize: 14,
            lineHeight: '12px',
            cursor: 'pointer',
            padding: '2px 4px',
            borderRadius: 4,
          }}
        >
          ×
        </span>
      )}
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 14, fontSize: 9, letterSpacing: '0.12em' }}>
        <span style={{ color: accent }}>{statusLabel(run.status)}</span>
        <span style={{ color: COLORS.textMuted }}>{mode}</span>
      </span>
      <span
        style={{
          display: 'block',
          marginTop: 2,
          color: terminalFailure ? COLORS.textMuted : COLORS.textPrimary,
          fontFamily: FONTS.mono,
          fontSize: 22,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.04em',
        }}
      >
        {terminalFailure ? '—' : formatTimedRunElapsed(timedRunElapsedMs(state))}
      </span>
    </button>
  );
};
